import type { CardDefinition } from '@/redux/internal-services/agentApi';
import { canonicalModule } from './promptPlacement';

/** A condition a block can use ("applies when") and the card it belongs to, built from the card catalogue. */
export interface ScreenCondition {
  label: string;
  expr: string;
  card: string;
  module: string;
}

export const humanize = (id: string): string => {
  const s = id.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/** Every "this card is open" / "this panel is open" condition the catalogue knows, so a new card or panel shows up by itself. */
export const screenConditions = (cards: CardDefinition[]): ScreenCondition[] =>
  cards.flatMap((c) => {
    const module = canonicalModule(c.module);
    return [
      { label: `${c.id} is open`, expr: `screen.card == '${c.id}'`, card: c.id, module },
      ...(c.components ?? []).map((comp) => ({
        label: `${humanize(comp.id)} panel (${c.id}) is open`,
        expr: `screen.open('${comp.id}')`,
        card: c.id,
        module,
      })),
    ];
  });

/** A condition in words: each known piece replaced by its label, "and / or / not" kept. */
export const describeWhen = (expr: string | undefined, conds: ScreenCondition[]): string => {
  let out = (expr ?? '').trim();
  if (!out) return '';
  [...conds].sort((a, b) => b.expr.length - a.expr.length).forEach((c) => { out = out.split(c.expr).join(c.label); });
  return out;
};

interface BlockLike {
  applies_when?: string;
  topic_cards?: string[];
  home_module?: string;
}

/** A block shown in the screen-blocks list: it has a condition or card keys, and is not a module General block. */
export const isScreenBlock = (b: BlockLike): boolean =>
  !b.home_module && (!!(b.applies_when ?? '').trim() || (b.topic_cards ?? []).some(Boolean));

/** The module a block belongs to for filtering only: found from its condition, else from its card keys. */
export const blockModuleOf = (b: BlockLike, conds: ScreenCondition[]): string | null => {
  const when = b.applies_when ?? '';
  const hit = conds.find((c) => when.includes(c.expr));
  if (hit) return hit.module;
  const key = (b.topic_cards ?? []).find(Boolean);
  const card = key?.split('#')[0];
  return conds.find((c) => c.card === card)?.module ?? null;
};
